import { Tab } from "@headlessui/react";
import CommentListing from "components/CommentListing/CommentListing";
import ExperiencesCard from "components/ExperiencesCard/ExperiencesCard";
import StartRating from "components/StartRating/StartRating";
import StayCard from "components/StayCard/StayCard";
import { FC, Fragment, useEffect, useState } from "react";
import Avatar from "shared/Avatar/Avatar";
import SocialsList from "shared/SocialsList/SocialsList";
import { Helmet } from "react-helmet";
import moment from "moment";
import { useGetHostQuery, useGetMeQuery } from "application/features/host/hostApiSlice";
import { useDispatch } from "react-redux";
import { setHost } from "application/features/host/hostSlice";
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useParams } from "react-router-dom";
import { useGetReviewsByHostUUIDQuery, useLazyGetReviewsByHostUUIDQuery } from "application/features/review/reviewApiSlice";
import Pagination from "shared/Pagination/Pagination";
import { useGetPropertiesByHostQuery } from "application/features/property/propertyApiSlice";
import { useGetExperiencesByHostUUIDQuery } from "application/features/experience/experienceApiSlice";
import { useTranslation } from "react-i18next";

export interface HostProfileProps {
  className?: string;
}

const HostProfile: FC<HostProfileProps> = ({ className = "" }) => {

  const { t, ready } = useTranslation("translation");

  const { host_uuid } = useParams<{ host_uuid: string }>();

  const { data: host, isLoading, isError } = useGetHostQuery(host_uuid)
  const { data: properties, isLoading: isLoadingProperties } = useGetPropertiesByHostQuery(host_uuid)
  const { data: experiences, isLoading: isLoadingExperiences } = useGetExperiencesByHostUUIDQuery(host_uuid)

  const [fetchReviews, { data: reviews, isLoading: isLoadingReviews, isSuccess: isSuccessReviews }] = useLazyGetReviewsByHostUUIDQuery();

  const dispatch = useDispatch()

  useEffect(() => {
    fetchReviews({ host_uuid, page: 1 })
  }, [host_uuid])

  useEffect(() => {
    if (host) {
      dispatch(setHost({ ...host }))
    }
  }, [isLoading]);

  let [categories] = useState(["Strutture", "Esperienze"]);

  if (!ready) return null;

  const renderSidebar = () => {
    return (
      <div className=" w-full flex flex-col items-center text-center sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-6 sm:space-y-7 px-0 sm:p-6 xl:p-8">
        <Avatar
          hasChecked
          hasCheckedClass="w-6 h-6 -top-0.5 right-2"
          sizeClass="w-28 h-28"
          imgUrl={host?.avatar ? host.avatar : ""}
        />

        {/* ---- */}
        <div className="space-y-3 text-center flex flex-col items-center">
          <h2 className="text-3xl font-semibold">{host?.name}</h2>
          <StartRating point={host?.rating.toFixed(1)} reviewCount={host?.reviews_count} className="!text-base" />
        </div>

        {/* ---- */}
        <p className="text-neutral-500 dark:text-neutral-400">
          {host?.description}
        </p>

        {/* ---- */}
        {host?.socials &&
          <SocialsList
            className="!space-x-3"
            itemClass="flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 dark:bg-neutral-800 text-xl"
          />
        }

        {/* ---- */}
        <div className="border-b border-neutral-200 dark:border-neutral-700 w-14"></div>

        {/* ---- */}
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            <span className="text-neutral-6000 dark:text-neutral-300 capitalize">
              {host?.city} - {host?.state}
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {
              host?.created_at &&
              <span className="text-neutral-6000 dark:text-neutral-300">
                {t("translation:hostProfilePage.sidebar.memberSince")} {moment(host?.created_at).format("DD MMM YYYY")}
              </span>
            }
          </div>
        </div>
      </div>
    );
  };

  const renderSection1 = () => {
    return (
      <div className="listingSection__wrap">
        <div>
          <h2 className="text-2xl font-semibold">{t("translation:hostProfilePage.services.title")}</h2>
          <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
            {host?.name} {t("translation:hostProfilePage.services.description")}
          </span>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        <div>
          <Tab.Group>
            <Tab.List className="flex space-x-1 overflow-x-auto">
              {categories.map((item) => (
                <Tab key={item} as={Fragment}>
                  {({ selected }) => (
                    <button
                      className={`flex-shrink-0 block !leading-none font-medium px-5 py-2.5 text-sm sm:text-base sm:px-6 sm:py-3 capitalize rounded-full focus:outline-none ${selected
                        ? "bg-secondary-900 text-secondary-50 "
                        : "text-neutral-500 dark:text-neutral-400 dark:hover:text-neutral-100 hover:text-neutral-900 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        } `}
                    >
                      {item}
                    </button>
                  )}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel className="">
                <div className="mt-8 grid grid-cols-1 gap-6 md:gap-7 sm:grid-cols-2">
                  {
                    isLoadingProperties ? Array.from({ length: 4 }).map((_, i) => (
                      <StayCard key={i} data={{}} />
                    )) :
                      properties?.map((property: any) => (
                        <StayCard key={property.property_uuid} data={property}  />
                      ))
                  }
                </div>
              </Tab.Panel>
              <Tab.Panel className="">
                <div className="mt-8 grid grid-cols-1 gap-6 md:gap-7 sm:grid-cols-2">
                {
                    isLoadingExperiences ? Array.from({ length: 4 }).map((_, i) => (
                      <StayCard key={i} data={{}} />
                    )) :
                      experiences?.map((experience: any) => (
                        <ExperiencesCard key={experience.experience_uuid} data={experience}  />
                      ))
                  }
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    );
  };

  const renderSection2 = () => {

    if (reviews?.data?.length === 0) {
      return null;
    }
    
    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <h2 className="text-2xl font-semibold">{t("translation:hostProfilePage.reviews.title")}</h2>
        <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
          {t("translation:hostProfilePage.reviews.description")}
        </span>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        {/* comment */}
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {
            reviews?.data?.map((review: any) => (
              <CommentListing key={review.id} data={review} />
            ))
          }
        </div>
        <div className="w-full items-center justify-center flex">
          <Pagination
            totalPages={reviews?.totalPages || 0}
            currentPage={reviews?.currentPage || 0}
            onPageChange={(page) => fetchReviews({ host_uuid: host_uuid, page })}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={`nc-HostProfile ${className}`} data-nc-id="HostProfile">
      <Helmet>
        <title>{t("translation:hostProfilePage.title")} || TRIBYOU</title>
      </Helmet>
      <main className="container mt-12 mb-24 lg:mb-32 flex flex-col lg:flex-row">
        <div className="block flex-grow mb-24 lg:mb-0">
          <div className="lg:sticky lg:top-24">
            {
              !isLoading ? renderSidebar() : <Skeleton className="h-[20rem]" />
            }
          </div>
        </div>
        <div className="w-full lg:w-3/5 xl:w-2/3 space-y-8 lg:space-y-10 lg:pl-10 flex-shrink-0">
          {
            renderSection1()
          }
          {
            (host && !isLoading) ? renderSection2() : <Skeleton className="h-[20rem]" />
          }
        </div>
      </main>
    </div>
  );
};

export default HostProfile;
